import { createListenerMiddleware, createSelector, createSlice, isAnyOf, PayloadAction } from "@reduxjs/toolkit";
import { RootState, store } from ".";
import {
  fetchSignInMethodsForEmail,
  getAuth,
  GithubAuthProvider,
  GoogleAuthProvider,
  linkWithCredential,
  OAuthProvider,
  SAMLAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import { getToken, request } from "../services/cpln";
import { jwtDecode } from "jwt-decode";
import {
  BASE_URL,
  HUBSPOT_SERVICE_URL,
  IS_DEPLOYMENT_ENV_PROD,
  IS_DEPLOYMENT_ENV_TEST,
  IS_HUBSPOT_INTEGRATION_ENABLED,
  IS_SENTRY_ENABLED,
  STORAGE_KEY_LOGGED_OUT_STATE,
} from "../envVariables";
import {
  fetchAccountSummaries,
  fetchAdminOrg,
  fetchOrgInvites,
  fetchOrgs,
  selectHasAccount,
  selectHasOrg,
} from "./userDataSlice";
import { getAnalytics, setUserId, setUserProperties } from "firebase/analytics";
import { sentryLog } from "../tools/sentry";
import { getFirebaseApp } from "./firebase";
import { initializeContext, setOrgAndGvc } from "./contextSlice";
import { Org } from "../schema/types/org/entity";
import { setInitialUrl, updateLastActivityTimestamp } from "./uiDataSlice";
import { getReferralCookies } from "../services/utils";

export type UserType = "none" | "user" | "impersonate" | "inspect";

export interface UserLoginInfo {
  email: string;
  name: string;
  photoUrl: string;
  uid: string;
}

export type UserProvider = "controlplane" | "aws";

interface UserState extends UserLoginInfo {
  provider: UserProvider;
  firebaseInitialized: boolean;

  type: UserType;
  isSaml: boolean;
  samlDomain: string;

  // admin panel related
  expires: number;
  key: string;
  org: string;

  // flags
  firstCheckDone: boolean;
  isProcessing: boolean;
}

interface SetUserPayload {
  user: UserLoginInfo | null;
  extra: {
    type: UserType;
    expires?: number;
    key?: string;
    org?: string;
  };
}

const initialState: UserState = {
  provider: "controlplane",
  firebaseInitialized: false,
  type: "none",
  email: "",
  name: "",
  photoUrl: "",
  uid: "",
  isSaml: false,
  samlDomain: "",

  // admin panel related
  expires: 0,
  key: "",
  org: "",

  // flags
  firstCheckDone: false,
  isProcessing: false,
};

export const userSlice = createSlice({
  name: "uiData",
  initialState,
  reducers: {
    initializeUserProvider(state) {
      state.provider = window.location.pathname.startsWith(`/provider/aws`) ? "aws" : "controlplane";
    },
    setFirstCheckDone(state, action: PayloadAction<boolean>) {
      state.firstCheckDone = action.payload;
    },
    setFirebaseInitialized(state, action: PayloadAction<boolean>) {
      state.firebaseInitialized = action.payload;
    },
    setProvider(state, action: PayloadAction<UserProvider>) {
      state.provider = action.payload;
    },
    setUser(state, action: PayloadAction<SetUserPayload>) {
      if (action.payload.user === null) {
        state.type = "none";
        state.email = "";
        state.name = "";
        state.photoUrl = "";
        state.uid = "";
        return;
      }

      state.type = "user";
      state.email = action.payload.user.email;
      state.name = action.payload.user.name;
      state.photoUrl = action.payload.user.photoUrl;
      state.uid = action.payload.user.uid;
      state.type = action.payload.extra.type;

      state.expires = action.payload.extra.expires || 0;
      state.key = action.payload.extra.key || "";
      state.org = action.payload.extra.org || "";
      if (action.payload.extra.key) {
        state.firstCheckDone = true;
      }
    },
    setSaml(state, action: PayloadAction<{ isSaml: boolean; samlDomain: string }>) {
      state.isSaml = action.payload.isSaml;
      state.samlDomain = action.payload.samlDomain;
    },
    setIsProcessing(state, action: PayloadAction<boolean>) {
      state.isProcessing = action.payload;
    },
  },
});

const selectName = (state: RootState) => state.user.name;
const selectEmail = (state: RootState) => state.user.email;
const selectType = (state: RootState) => state.user.type;
export const selectIsAdmin = (state: RootState) => state.user.type === "impersonate" || state.user.type === "inspect";

export const selectIsLoggedIn = createSelector([selectType, selectIsAdmin], (type, isAdmin) => {
  return isAdmin || type === "user";
});

export const selectAvatarInitials = createSelector([selectName, selectEmail], (name, email) => {
  if (name.length > 0) {
    return name
      .split(" ")
      .filter((p) => p.length > 0)
      .map((p) => p.slice(0, 1))
      .join("")
      .toUpperCase();
  }
  if (email.length > 0) {
    return email.slice(0, 1).toUpperCase();
  }
  return "";
});

export const selectIsCplnTeam = createSelector([selectIsLoggedIn, selectEmail], (isLoggedIn, email) => {
  if (!isLoggedIn) {
    return false;
  }
  return email.includes("@controlplane.com");
});

export async function handleSignOut(keepUrl: boolean) {
  console.log("@handleSignOut");
  const state = store.getState();
  if (state.user.type !== "user") {
    return;
  }

  await signOut(getAuth(getFirebaseApp()));

  if (keepUrl) {
    store.dispatch(setInitialUrl(window.location.pathname));
  } else {
    window.history.replaceState(null, "", "/");
  }

  try {
    setUserProperties(getAnalytics(getFirebaseApp()), {});
    setUserId(getAnalytics(getFirebaseApp()), "");
  } catch (e) {}
}

export const {
  initializeUserProvider,
  setFirstCheckDone,
  setFirebaseInitialized,
  setProvider,
  setUser,
  setIsProcessing,
  setSaml,
} = userSlice.actions;

export const userListenerMiddleware = createListenerMiddleware();

userListenerMiddleware.startListening({
  actionCreator: setUser,
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState() as RootState;
    const user = action.payload.user;
    const type = state.user.type;

    if (!user || type !== "user") {
      // Hiding Sentry's user feedback button
      const feedbackDiv = document.getElementById("feedback-button");
      if (feedbackDiv) {
        feedbackDiv.style.display = "none";
      }
    }

    if (!user) {
      return;
    }

    listenerApi.dispatch(setIsProcessing(true));

    const token = await getToken();
    try {
      const decodedToken: any = jwtDecode(token);
      let isSaml = ((decodedToken?.firebase?.sign_in_provider as string) || "").startsWith("saml.");
      let samlDomain = "";
      if (isSaml) {
        samlDomain = ((decodedToken?.firebase?.sign_in_provider as string) || "").slice(5);
      }
      listenerApi.dispatch(setSaml({ isSaml: isSaml, samlDomain: samlDomain }));
    } catch (e) {}

    if (type === "user") {
      // touch base
      try {
        await fetch(`${BASE_URL}/user`, { method: "post", headers: { Authorization: token } });
      } catch (e) {
        sentryLog("sign out, touch base failed");
        await handleSignOut(false);
        return;
      }

      // touch hubspot service for referral record, doesnt touch hubspot anymore
      touchHubspotForReferral(state);
    }

    let contextPromise;
    if (type === "user") {
      const responses = await Promise.all([
        listenerApi.dispatch(fetchAccountSummaries()),
        listenerApi.dispatch(fetchOrgs()),
        listenerApi.dispatch(fetchOrgInvites()),
      ]);
      const orgs: Org[] = responses[1].payload as Org[];
      contextPromise = listenerApi.dispatch(initializeContext(orgs.map((o) => o.name))).unwrap();
    } else {
      const org = await listenerApi.dispatch(fetchAdminOrg()).unwrap();
      listenerApi.dispatch(setOrgAndGvc({ org: org.name, gvc: "" }));
    }

    listenerApi.dispatch(updateLastActivityTimestamp());

    if (type === "user") {
      try {
        if (IS_DEPLOYMENT_ENV_PROD) {
          // @ts-ignore
          window.dataLayer.push({
            event: "userSignedIn",
          });
        }
      } catch (e) {
        console.error("data layer userSignedIn event failed", e.message);
      }
    }

    let preventHubspot = false;
    const isLoggedIn = selectIsLoggedIn(state);
    const isCplnTeam = selectIsCplnTeam(state);
    const hasAccount = selectHasAccount(state);
    const hasOrg = selectHasOrg(state);
    if (type !== "user" || !user || !isLoggedIn || isCplnTeam || hasAccount || hasOrg) {
      preventHubspot = true;
    }

    // Touch hubspot
    if (IS_HUBSPOT_INTEGRATION_ENABLED && !preventHubspot) {
      let hubspotutk = "";
      try {
        let _hubspotutk = document.cookie
          .split(" ")
          .find((c) => c.startsWith("hubspotutk"))!
          .split("=")[1];
        if (_hubspotutk.endsWith(";")) {
          _hubspotutk = _hubspotutk.split(";")[0];
        }
        hubspotutk = _hubspotutk;
      } catch (e) {}

      request({
        service: "self",
        method: "post",
        url: `${HUBSPOT_SERVICE_URL}/firebasesignin`,
        body: {
          account: { fullName: user.name, email: user.email },
          hubspotutk,
          environment: IS_DEPLOYMENT_ENV_TEST ? "test" : "production",
        },
      }).catch(() => {
        // TODO keep this in sentry
        console.error("Failed to inform hubspot");
      });
    }

    if (contextPromise) {
      await contextPromise;
    }
    listenerApi.dispatch(setIsProcessing(false));

    // Showing Sentry's user feedback button if it is active
    if (IS_SENTRY_ENABLED && type === "user") {
      const feedbackDiv = document.getElementById("feedback-button");
      if (feedbackDiv) {
        feedbackDiv.style.display = "block";
      }
    }

    localStorage.setItem(STORAGE_KEY_LOGGED_OUT_STATE, "false");
    if (type === "user") {
      const userAnalyticsData = { email: user.email, username: user.name, id: user.uid };
      setUserProperties(getAnalytics(getFirebaseApp()), userAnalyticsData);
      setUserId(getAnalytics(getFirebaseApp()), userAnalyticsData.id);
    }
  },
});

export const userReducer = userSlice.reducer;

async function touchHubspotForReferral(state: RootState) {
  try {
    const HUBSPOT_USER_STORAGE_KEY = `HUBSPOT_USER_${state.user.email}`;
    const isAlreadyCalled = localStorage.getItem(HUBSPOT_USER_STORAGE_KEY) === "true";
    if (IS_HUBSPOT_INTEGRATION_ENABLED && !isAlreadyCalled && !selectIsCplnTeam(state)) {
      let externalId = "";
      if (state.uiData.initialUrl.includes("?id=")) {
        externalId = state.uiData.initialUrl.split("?id=")[1] || "";
      }
      const referralCookie = getReferralCookies().pVisitorId;
      const { data } = await request({
        service: "self",
        method: "post",
        url: `${HUBSPOT_SERVICE_URL}/user`,
        body: { email: state.user.email, name: state.user.name, leadSource: "Console", externalId, referralCookie },
      });
      if (data.success === true) {
        localStorage.setItem(HUBSPOT_USER_STORAGE_KEY, "true");
      } else {
        console.error("Failed to touch hubspot user", data.error);
      }
    }
  } catch (e) {
    console.error("Failed to touch hubspot user", e.message);
  }
}

export async function approveLinking(provider: any, pendingCredential: any) {
  try {
    await signInWithPopup(getAuth(getFirebaseApp()), provider);
    const user = getAuth(getFirebaseApp()).currentUser;
    if (user) {
      await linkWithCredential(user, pendingCredential);
    }
  } catch (e) {
    // notification.error({
    //   message: "Failed to link provider",
    //   description: e.message,
    // });
  }
}

export async function signInEmail(email: string, password: string) {
  try {
    await signInWithEmailAndPassword(getAuth(getFirebaseApp()), email, password);
  } catch (e) {
    throw e;
  }
}

export async function signInSaml(email: string, providerOverride: string) {
  try {
    let provider = providerOverride;
    if (!provider) {
      const { data } = await request({ url: `/auth/details/${email}` });
      provider = data.provider;
    }
    await signInWithPopup(getAuth(getFirebaseApp()), new SAMLAuthProvider(provider));
  } catch (e) {
    throw e;
  }
}

export async function signInByProvider(provider: "google" | "microsoft" | "github") {
  const githubProvider = new GithubAuthProvider();
  const microsoftProvider = new OAuthProvider("microsoft.com").setCustomParameters({
    prompt: "select_account",
  });
  const googleProvider = new GoogleAuthProvider().setCustomParameters({ prompt: "select_account" });
  try {
    let firebaseProvider: any = null as any;
    switch (provider) {
      case "google":
        firebaseProvider = googleProvider;
        break;
      case "github":
        firebaseProvider = githubProvider;
        break;
      case "microsoft":
        firebaseProvider = microsoftProvider;
        break;
    }

    await signInWithPopup(getAuth(getFirebaseApp()), firebaseProvider);
  } catch (e) {
    if (e.code !== "auth/account-exists-with-different-credential") {
      throw e;
    }

    const { credential: pendingCredential, email } = e;
    // Get sign-in methods for this email.
    const methods = await fetchSignInMethodsForEmail(getAuth(getFirebaseApp()), email);
    const method = methods[0];

    if (methods[0] === "password") {
      throw e;
    }

    const providerForMethod = method.includes("google")
      ? googleProvider
      : method.includes("github")
      ? githubProvider
      : method.includes("microsoft")
      ? microsoftProvider
      : null;
    if (!providerForMethod) {
      throw e;
    }

    // const m = Modal.confirm({
    //   title: "Linking Account",
    //   content: `You already have an account with the provider ${method}, to link your new provider, please confirm by signing in with old provider.`,
    //   footer: (
    //     <div className="modal-actions">
    //       <NGButton
    //         variant="secondary"
    //         onClick={() => {
    //           this.cancelLinking();
    //           m.destroy();
    //         }}
    //       >
    //         Cancel
    //       </NGButton>
    //       <NGButton
    //         variant="primary"
    //         onClick={() => {
    //           this.approveLinking(providerForMethod, pendingCredential);
    //           m.destroy();
    //         }}
    //       >
    //         Confirm
    //       </NGButton>
    //     </div>
    //   ),
    //   onCancel: () => {
    //     this.cancelLinking();
    //   },
    // });
  }
}
